// /admin/ads → "איפה נתקעים": new signups through the 3 onboarding steps (campaign → post →
// Facebook → paid), where each one stopped, whether they watched that step's video or asked
// for help (OnboardingEvent, from the in-app guide), and the stuck list to call/WhatsApp.
import {FC, useEffect, useState} from 'react'
import {getStuck, StuckResponse} from './_requests'

const STEP_LABEL: Record<string, string> = {
  campaign: 'קמפיין',
  post: 'פוסט',
  facebook: 'חיבור פייסבוק',
}
const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0)
const waLink = (phone: string, name: string, step: string) => {
  const d = String(phone || '').replace(/\D/g, '')
  const intl = d.startsWith('0') ? '972' + d.slice(1) : d
  const text = `היי ${name.split(' ')[0] || ''}, כאן FBZipper 🙂 ראיתי שנעצרת בשלב "${
    STEP_LABEL[step]
  }" — אפשר לעזור לך להשלים את זה עכשיו?`
  return `https://wa.me/${intl}?text=${encodeURIComponent(text)}`
}

const Stuck: FC = () => {
  const [days, setDays] = useState(30)
  const [source, setSource] = useState<'all' | 'ads'>('all')
  const [data, setData] = useState<StuckResponse | null>(null)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState<string>('')

  useEffect(() => {
    setData(null)
    getStuck(days, source)
      .then(({data}) => setData(data))
      .catch((e) => setError(e?.response?.data?.error || e.message))
  }, [days, source])

  if (error) return <div className='alert alert-danger'>{error}</div>
  if (!data) return <div className='text-muted'>טוען…</div>

  const s = data.summary
  const bars: [string, number][] = [
    ['נרשמו', s.signups],
    ['יצרו קמפיין', s.steps.campaign.reached],
    ['הוסיפו פוסט', s.steps.post.reached],
    ['חיברו פייסבוק', s.steps.facebook.reached],
  ]
  const rows = data.rows.filter((r) => !filter || r.stuck_at === filter)

  return (
    <div>
      <div className='d-flex gap-2 align-items-center flex-wrap mb-4'>
        <div className='btn-group btn-group-sm'>
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              className={`btn ${days === d ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setDays(d)}
            >
              {d} ימים
            </button>
          ))}
        </div>
        <div className='btn-group btn-group-sm'>
          <button
            className={`btn ${source === 'all' ? 'btn-primary' : 'btn-light'}`}
            onClick={() => setSource('all')}
          >
            כל הנרשמים
          </button>
          <button
            className={`btn ${source === 'ads' ? 'btn-primary' : 'btn-light'}`}
            onClick={() => setSource('ads')}
          >
            רק ממודעות
          </button>
        </div>
        <span className='text-muted fs-8'>חשבונות בדיקה לא נספרים</span>
      </div>

      <div className='card card-bordered p-5 mb-5'>
        <h4 className='fw-bold mb-4'>כמה מגיעים לכל שלב</h4>
        {bars.map(([label, n], i) => (
          <div key={label} className='d-flex align-items-center gap-3 mb-2'>
            <span className='fs-7 fw-bold' style={{width: 110}}>
              {label}
            </span>
            <div
              className='flex-grow-1'
              style={{background: '#f1f3f9', borderRadius: 8, height: 28, position: 'relative'}}
            >
              <div
                style={{
                  width: `${pct(n, s.signups)}%`,
                  height: '100%',
                  borderRadius: 8,
                  background: i === 0 ? '#94a3b8' : 'linear-gradient(90deg,#2b70fa,#7c5cff)',
                }}
              />
              <span
                className='fs-8 fw-bold'
                style={{position: 'absolute', insetInlineStart: 10, top: 5, color: '#0e1133'}}
              >
                {n} ({pct(n, s.signups)}%)
              </span>
            </div>
          </div>
        ))}
      </div>

      <div
        className='d-grid gap-4 mb-5'
        style={{gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))'}}
      >
        {(['campaign', 'post', 'facebook'] as const).map((k) => {
          const st = s.steps[k]
          return (
            <button
              key={k}
              className={`card card-bordered p-4 text-start ${
                filter === k ? 'border-primary border-2' : ''
              }`}
              onClick={() => setFilter(filter === k ? '' : k)}
              style={{background: '#fff'}}
            >
              <div className='text-muted fs-8'>נתקעו בשלב</div>
              <div className='fw-bold fs-4 mb-1'>{STEP_LABEL[k]}</div>
              <div className='fs-1 fw-bolder text-danger'>{st.stuck}</div>
              <div className='fs-8 text-muted mt-2'>
                מתוכם צפו בסרטון: <b>{pct(st.stuck_watched, st.stuck)}%</b> · ביקשו עזרה:{' '}
                <b>{pct(st.stuck_helped, st.stuck)}%</b>
              </div>
              <div className='fs-9 text-primary mt-1'>
                {filter === k ? 'מציג רק אותם ✓' : 'לחצו לסינון הרשימה'}
              </div>
            </button>
          )
        })}
      </div>

      <h4 className='fw-bold mb-1'>מי נתקע — לחזור אליהם</h4>
      <div className='text-muted fs-7 mb-3'>
        "צפה בסרטון" ו"ביקש עזרה" נאספים מהמדריך באפליקציה (ממועד ההשקה). לחיצה על וואטסאפ פותחת
        הודעה מוכנה לפי השלב.
      </div>
      <div className='table-responsive'>
        <table className='table table-sm table-row-bordered align-middle fs-7'>
          <thead>
            <tr className='fw-bold text-muted'>
              <th>שם</th>
              <th>נרשם</th>
              <th>מקור</th>
              <th>נתקע ב</th>
              <th>צפה בסרטון</th>
              <th>ביקש עזרה</th>
              <th>פעילות אחרונה</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r._id}>
                <td>
                  <a href={`/admin/accounts/${r._id}`}>{r.name || 'ללא שם'}</a>
                  {r.device && (
                    <span className='text-muted fs-9'>
                      {' '}
                      · {r.device === 'mobile' ? '📱' : '💻'}
                    </span>
                  )}
                </td>
                <td>{new Date(r.created_at).toLocaleDateString('he-IL')}</td>
                <td className='fs-8'>
                  {r.source}
                  {r.ad_name ? (
                    <div className='text-muted text-truncate' style={{maxWidth: 160}}>
                      {r.ad_name}
                    </div>
                  ) : null}
                </td>
                <td>
                  <span className='badge badge-light-danger'>{STEP_LABEL[r.stuck_at]}</span>
                </td>
                <td>{r.watched.includes(r.stuck_at) ? '✓' : '—'}</td>
                <td>{r.helped.includes(r.stuck_at) ? '✓' : '—'}</td>
                <td className='fs-8'>
                  {r.last_activity ? new Date(r.last_activity).toLocaleString('he-IL') : '—'}
                </td>
                <td>
                  {r.phone && (
                    <a
                      className='btn btn-sm btn-light-success'
                      href={waLink(r.phone, r.name, r.stuck_at)}
                      target='_blank'
                      rel='noreferrer'
                    >
                      <i className='bi bi-whatsapp' />
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default Stuck
